import { useClipboard } from '@mantine/hooks';
import { Box, Fade, Stack, Tab, Tabs, Typography } from '@mui/material';
import { openUrl } from '@tauri-apps/plugin-opener';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ScanWarningDialog } from '@ui/Components/Scan/ScanWarningDialog';
import { useHasNext } from '@ui/Hooks/useHasNext';
import { extractSerialFromUrl } from '@ui/Utils/scan/tag/extractSerial';
import { TagResultState, isTagResultState } from '@ui/Utils/scan/tag/scanTypes';

import AdvancedTagContent from './AdvancedTagContent';
import TagFlowResultContent from './TagFlowResultContent/index';

function useTagResultState(): TagResultState | null {
    const loc = useLocation();
    const st: unknown = loc.state;
    if (!isTagResultState(st)) {
        return null;
    }
    const serial =
        st.type === 'nfc'
            ? (st.uid?.replaceAll(':', '').toLowerCase().replace(/^0+/, '') ?? null)
            : st.redirectUrl
              ? extractSerialFromUrl(st.redirectUrl)
              : null;
    return {
        error: st.error ?? null,
        ok: st.ok,
        raw: st.raw ?? null,
        redirectUrl: st.redirectUrl ?? null,
        serial: serial,
        tech: st.tech ?? null,
        texts: st.texts ?? null,
        type: st.type,
        uid: st.uid ?? null,
    };
}

export default function TagResultView() {
    const navigate = useNavigate();
    const hasNext = useHasNext();
    const data = useTagResultState();
    const [infoOpen, setInfoOpen] = useState(false);
    const [tabIndex, setTabIndex] = useState(0);

    useEffect(() => {
        if (!data) {
            navigate('/scan', { replace: true });
        }
    }, [data, navigate]);

    const openInfo = useCallback(() => setInfoOpen(true), []);
    const closeInfo = useCallback(() => setInfoOpen(false), []);
    const handleTabChange = useCallback((_e: React.SyntheticEvent, v: number) => setTabIndex(v), []);
    const goBackToScan = useCallback(() => {
        navigate('/scan');
    }, [navigate]);

    const urlClipboard = useClipboard({ timeout: 1500 });
    const uidClipboard = useClipboard({ timeout: 1500 });

    const copyUrl = useCallback(() => {
        if (data?.redirectUrl) {
            urlClipboard.copy(data.redirectUrl);
        } else {
            if (data?.raw) {
                urlClipboard.copy(data.raw);
            }
        }
    }, [data, urlClipboard]);

    const onOpenLink = () => {
        if (data?.redirectUrl) {
            void openUrl(data.redirectUrl);
        }
    };

    const copyUid = useCallback(() => {
        if (data?.uid) {
            const cleaned = data.uid.replaceAll(':', '').toLowerCase();
            uidClipboard.copy(cleaned);
        }
    }, [data, uidClipboard]);

    const showTabs = Boolean(hasNext && data);

    return (
        <Fade in>
            <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 4, width: '100%' }}>
                <Stack spacing={3} sx={{ height: '100%', mx: 'auto', width: '100%' }}>
                    {!data ? (
                        <Stack
                            spacing={2}
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <Typography variant="h6">Redirecting…</Typography>
                            <Typography
                                sx={{
                                    color: 'text.secondary',
                                }}
                                variant="body2"
                            >
                                No tag data available. Returning to scan.
                            </Typography>
                        </Stack>
                    ) : !showTabs ? (
                        <TagFlowResultContent
                            data={data}
                            onInfo={openInfo}
                            onOpenLink={onOpenLink}
                            onScanAgain={goBackToScan}
                        />
                    ) : (
                        <>
                            <Tabs
                                onChange={handleTabChange}
                                sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
                                value={tabIndex}
                                variant="fullWidth"
                            >
                                <Tab label="Result" />
                                <Tab label={data.type === 'nfc' ? 'Advanced NFC' : 'Advanced'} />
                            </Tabs>
                            <Box
                                sx={{
                                    flex: 1,
                                    pb: 2,
                                    px: 2,
                                }}
                            >
                                <Box
                                    hidden={tabIndex !== 0}
                                    role="tabpanel"
                                    sx={{ display: tabIndex === 0 ? 'block' : 'none', height: '100%' }}
                                >
                                    <TagFlowResultContent
                                        data={data}
                                        onInfo={openInfo}
                                        onOpenLink={onOpenLink}
                                        onScanAgain={goBackToScan}
                                    />
                                </Box>
                                {tabIndex === 1 ? (
                                    <AdvancedTagContent
                                        data={data}
                                        onCopyUid={copyUid}
                                        onCopyUrl={copyUrl}
                                        uidCopied={uidClipboard.copied}
                                        urlCopied={urlClipboard.copied}
                                    />
                                ) : null}
                            </Box>
                        </>
                    )}
                </Stack>
                <ScanWarningDialog onClose={closeInfo} open={infoOpen} />
            </Box>
        </Fade>
    );
}
